/**
 * KYC Queue — ISO/Super Admin compliance oversight across the team.
 */
import React, { useEffect, useState } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { ShieldCheck, Clock, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { KYCChecklistPanel } from '../components/kyc/KYCChecklistPanel';

function ChecklistCard({ c, onOpen }) {
  const doneCount = c.items.filter((i) => i.status === 'approved').length;
  return (
    <button
      onClick={() => onOpen(c)}
      data-testid={`kyc-queue-card-${c.id}`}
      className="w-full text-left rounded-lg border border-border p-3 hover:border-primary/40 transition-colors"
    >
      <p className="text-sm font-medium">{c.business_name}</p>
      <p className="text-xs text-muted-foreground mt-0.5">{doneCount}/{c.items.length} items approved</p>
    </button>
  );
}

export default function KYCQueuePage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [openChecklist, setOpenChecklist] = useState(null);

  const load = async () => {
    try {
      const d = await api.getKycQueue();
      setData(d);
      setError(false);
    } catch (e) {
      toast.error('Failed to load KYC queue');
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading) return <div className="py-24 text-center text-muted-foreground" data-testid="kyc-queue-loading">Loading compliance queue…</div>;

  if (error || !data) {
    return (
      <div className="py-24 text-center space-y-3" data-testid="kyc-queue-error">
        <p className="text-sm text-muted-foreground">Couldn't load the compliance queue.</p>
        <button onClick={load} className="text-sm text-primary underline" data-testid="kyc-queue-retry-btn">Try again</button>
      </div>
    );
  }

  const sections = [
    { key: 'pending_review', label: 'Pending Your Review', icon: Clock, color: 'text-amber-500' },
    { key: 'incomplete', label: 'Incomplete', icon: AlertTriangle, color: 'text-red-500' },
    { key: 'ready_to_submit', label: 'Ready to Submit', icon: CheckCircle2, color: 'text-emerald-500' },
  ];

  return (
    <div className="space-y-6" data-testid="kyc-queue-page">
      <div>
        <h1 className="text-2xl font-heading font-bold flex items-center gap-2"><ShieldCheck className="h-6 w-6" /> KYC / Compliance Queue</h1>
        <p className="text-sm text-muted-foreground mt-1">Dual sign-off oversight across your team.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {sections.map(({ key, label, icon: Icon, color }) => (
          <div key={key} className="space-y-2">
            <div className="flex items-center gap-2 text-sm font-semibold">
              <Icon className={`h-4 w-4 ${color}`} /> {label} <Badge variant="outline">{data[key]?.length || 0}</Badge>
            </div>
            <div className="space-y-2">
              {(data[key] || []).length === 0 ? (
                <p className="text-xs text-muted-foreground py-4">Nothing here</p>
              ) : (
                data[key].map((c) => <ChecklistCard key={c.id} c={c} onOpen={setOpenChecklist} />)
              )}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!openChecklist} onOpenChange={(v) => !v && setOpenChecklist(null)}>
        <DialogContent data-testid="kyc-queue-detail-modal" className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{openChecklist?.business_name}</DialogTitle>
            <DialogDescription>Review and approve this merchant's KYC documents.</DialogDescription>
          </DialogHeader>
          {openChecklist && (
            <KYCChecklistPanel
              applicationId={openChecklist.application_id}
              leadId={openChecklist.lead_id}
              onReady={() => {}}
              onChange={load}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
